import { useState, useMemo } from 'react';
import { Typography, Grid } from '@mui/material';
import { useQueryClient } from 'react-query';
import { isDate } from 'validator';

// Our components
import Alert from 'components/Alert';
import { PrimaryButton } from 'components/Button/Button';
import CurrencyTextInput from 'components/Input/CurrencyTextInput';
import Dropdown from 'components/Dropdown/Dropdown';
import GetFullAddressPartialForm from 'components/Forms/addressRelated/GetFullAddressPartialForm';
import Input from 'components/Input/TextInput';
import Loader from 'components/Loader';
import LoanInquirySuccessModal from 'components/Modal/LoanInquiryConfirmationModal';
import OutstandingBalanceInput from 'components/Client/Onboarding/OutstandingBalanceInput';
import RateInput from 'components/Input/RateInput';
import StandardDatePicker from 'components/DatePicker/StandardDatePicker';
import SoraTextField from 'components/Input/SoraTextField';
import YearMonthDatePicker from 'components/DatePicker/YearMonthDatePicker';
import { TEXT } from 'components/Input/Types';

// Our Query Keys
import { ERROR_MESSAGE_DATA } from 'shared/query-keys';

// Our hooks 🪝
import useMutateCreateDeal from 'hooks/hubspot/useMutateCreateDeal';
import useMutateSaveNewLoanInquiry from 'hooks/newLoanInquiries/useMutateSaveNewLoanInquiry';
import useMutateUpdateLoanInquiry from 'hooks/newLoanInquiries/useMutateUpdatedLoanInquiry';
import useMutateSendGenericEmail from 'hooks/emails/useMutateSendGenericEmail';

// Util Hooks
import usePrepopulateState from 'hooks/prepopulate/usePrepopulateState';

// Utils
import { clearFormValues, getDateFromDateObject } from 'shared/utils';
import createDefaultCloseDate from 'shared/utils/hubspot/createDefaultCloseDate';
import formatCollection from 'shared/utils/formatting/currency/formatCollection';

// Our Emails
import lendingEmailTemplate from 'emails/lendingEmailTemplate';

// Constants
import {
	EDUCATION_LEVEL_OPTIONS,
	EMPLOYMENT_STATUS_OPTIONS,
	REFINANCE_OFFER_TYPE,
	STUDENT
} from 'shared/constants';

function StudentLoanRefinanceForm({
	advisorInfo,
	clientInfo,
	clientProfileData,
	isClientNameProvided,
	financialData,
	loanRequestId,
	loanType
}) {
	const queryClient = useQueryClient();
	const updateLoanInquiry = useMutateUpdateLoanInquiry();

	// API Calls
	const saveLoanInquiry = useMutateSaveNewLoanInquiry();
	const { isLoading: savingLoanInquiry } = saveLoanInquiry;

	// Mutations
	const sendGenericEmail = useMutateSendGenericEmail();
	const createDeal = useMutateCreateDeal();

	// Advisor Info
	const { advisorName, advisorEmail, advisorCompany, advisorWebsite } =
		advisorInfo;

	// Client Info
	const {
		annualIncome,
		assetsAmount,
		clientFullName,
		creditScoreValue,
		clientId,
		emailAddressValue,
		firstName,
		lastName
	} = clientInfo;

	const {
		currentLoanInterestRateValue,
		currentOutstandingBalance,
		refinancingTradelineId
	} = financialData;

	// Pre-populated state items
	const [employer, setEmployer] = useState('');
	const [employmentStatus, setEmploymentStatus] = useState('');
	const [educationLevel, setEducationLevel] = useState('');
	const [startDate, setStartDate] = useState('');
	const [school, setSchool] = useState('');
	const [graduationDate, setGraduationDate] = useState('');
	const [dob, setDob] = useState('');
	const [currentLoanType, setCurrentLoanType] = useState('');
	const [loanAmount, setLoanAmount] = useState('');
	const [currentInterestRate, setCurrentInterestRate] = useState('');
	const [rentOrOwn, setRentOrOwn] = useState('');
	const [monthlyPayment, setMonthlyPayment] = useState('');

	// Address related fields
	const [addressLine1, setAddressLine1] = useState('');
	const [addressLine2, setAddressLine2] = useState('');
	const [city, setCity] = useState('');
	const [state, setState] = useState('');
	const [zipCode, setZipCode] = useState('');

	const [additionalNotes, setAdditionalNotes] = useState('');
	const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);

	const isFormReady = useMemo(
		() => isClientNameProvided,
		[isClientNameProvided]
	);

	usePrepopulateState(
		[
			{
				setState: setCurrentInterestRate,
				value: `${currentLoanInterestRateValue}`
			},
			{
				setState: setLoanAmount,
				value: `${currentOutstandingBalance}`
			},
			{
				setState: setAddressLine1,
				value: `${clientProfileData?.address?.addressLine1}`
			},
			{
				setState: setAddressLine2,
				value: `${clientProfileData?.address?.addressLine2}`
			},
			{
				setState: setCity,
				value: `${clientProfileData?.address?.city}`
			},
			{
				setState,
				value: `${clientProfileData?.address?.state}`
			},
			{
				setState: setZipCode,
				value: `${clientProfileData?.address?.zipcode}`
			},
			{
				setState: setEducationLevel,
				value: `${clientProfileData?.highestDegree}`
			},
			{
				setState: setEmployer,
				value: `${clientProfileData?.employer}`
			},
			{
				setState: setDob,
				value: `${clientProfileData?.dob}`
			},
			{
				setState: setEmploymentStatus,
				value: `${clientProfileData?.employmentStatus}`
			},
			{
				setState: setSchool,
				value: `${clientProfileData?.school}`
			},
			{
				setState: setGraduationDate,
				value: `${clientProfileData?.graduationDate}`
			}
		],
		[
			currentLoanInterestRateValue,
			currentOutstandingBalance,
			clientProfileData
		]
	);

	const isValidDate = useMemo(() => {
		const isStringDate = typeof dob === 'string';
		const isDateType = dob instanceof Date;
		if (isStringDate && dob !== '') {
			const isDateCheckWithConversion = isDate(new Date(dob));
			return isDateCheckWithConversion;
		}

		if (isDateType) {
			return isDate(dob);
		}
		// if dob is null this returns false;
		return false;
	}, [dob]);

	const GRADUATION_YEAR_ONLY_VIEW = ['year']; // This is for the DatePicker of Graduation Year

	const sendFormData = () => {
		const sendToArray = ['lending@sorafinance.com'];
		const subject = `Student Loan Refinance Request - ${firstName} ${lastName}`;

		const emailTemplate = lendingEmailTemplate({
			advisorInfo,
			clientInfo,
			financialData,
			clientFullName,
			loanAmount,
			loanRelatedDetails: [
				`Client's rent or own status: ${rentOrOwn}`,
				`Client's monthly housing cost: ${monthlyPayment}`,
				`Client's Address: ${addressLine1} ${addressLine2} ${city}, ${state} ${zipCode}`,
				`Client's Employment status: ${employmentStatus}`,
				`Client's Employment Start year: ${getDateFromDateObject(
					// @ts-ignore
					startDate
				)}`,
				`Client's Employer: ${employer}`,
				`Client's Highest degree obtained: ${educationLevel}`,
				`Client's School attended: ${school}`,
				`Client's Graduation year: ${graduationDate}`,
				`Client's Current Loan Type: ${currentLoanType}`,
				`Client's Outstanding Balance: ${currentOutstandingBalance}`,
				`Client's Current Interest Rate: ${currentInterestRate}`
			],
			additionalNotes,
			lendingTypeRequested: 'Student Loan Refinance'
		});

		sendGenericEmail.mutate(
			{
				firstName,
				lastName,
				subject,
				message: emailTemplate,
				sendToArray
			},
			{
				onSuccess: () => {
					clearFormValues([
						setEmployer,
						setEmploymentStatus,
						setEducationLevel,
						setStartDate,
						setSchool,
						setGraduationDate,
						setDob,
						setCurrentLoanType,
						setCurrentInterestRate,
						setLoanAmount,
						setAddressLine1,
						setAddressLine2,
						setCity,
						setState,
						setZipCode,
						setAdditionalNotes,
						setMonthlyPayment,
						setRentOrOwn
					]);
				}
			}
		);
	};

	const submitNewLoanInquiry = () => {
		const newLoanInquiryData = {
			additionalNotes,
			addressLine1,
			addressLine2,
			city,
			clientId,
			currentOutstandingBalance,
			currentLoanType,
			currentInterestRate,
			dob,
			educationLevel,
			emailAddress: emailAddressValue,
			employer,
			employmentStatus,
			firstName,
			graduationDate,
			lastName,
			monthlyPayment,
			offerType: REFINANCE_OFFER_TYPE,
			rentOrOwn,
			school,
			startDate,
			state,
			totalAnnualIncome: annualIncome,
			totalAssetsValue: assetsAmount,
			tradelineType: STUDENT,
			tradelineId: refinancingTradelineId,
			zipCode
		};

		// Call mutation to update user's data
		saveLoanInquiry.mutate(newLoanInquiryData, {
			onSuccess: () => {
				setIsSuccessModalOpen(true);
			},
			onError: () => {
				// Set error message
				queryClient.setQueryData(
					ERROR_MESSAGE_DATA,
					'There was an error making your loan request.'
				);
			}
		});
	};

	const updateLoanRequest = () => {
		const updatedLoanInquiryData = {
			additionalNotes,
			addressLine1,
			addressLine2,
			city,
			clientId,
			currentOutstandingBalance,
			currentLoanType,
			currentInterestRate,
			dob,
			educationLevel,
			emailAddress: emailAddressValue,
			employer,
			employmentStatus,
			firstName,
			graduationDate,
			lastName,
			monthlyPayment,
			offerType: REFINANCE_OFFER_TYPE,
			rentOrOwn,
			school,
			startDate,
			state,
			totalAnnualIncome: annualIncome,
			totalAssetsValue: assetsAmount,
			tradelineType: STUDENT,
			zipCode
		};

		// Call mutation to update user's data
		updateLoanInquiry.mutate(
			{ loanRequestId, updatedLoanInquiryData },
			{
				onSuccess: () => {
					clearFormValues([
						setEmployer,
						setEmploymentStatus,
						setEducationLevel,
						setStartDate,
						setSchool,
						setGraduationDate,
						setDob,
						setCurrentLoanType,
						setLoanAmount,
						setCurrentInterestRate,
						setAddressLine1,
						setAddressLine2,
						setCity,
						setState,
						setZipCode,
						setAdditionalNotes,
						setMonthlyPayment,
						setRentOrOwn
					]);
				},
				onError: () => {
					// Set error message
					queryClient.setQueryData(
						ERROR_MESSAGE_DATA,
						'There was an error making your loan request.'
					);
				}
			}
		);
	};

	const { isLoading } = sendFormData;

	const currentURL = window.location.href;
	const atLoanRequestDetailsUrl = currentURL.includes('loan-request-details');

	const submitForm = (event) => {
		event.preventDefault();

		const closeDate = createDefaultCloseDate();
		const valuesToFormat = [
			{
				type: 'currency',
				value: annualIncome,
				label: 'Annual Income:'
			},
			{
				type: 'currency',
				value: assetsAmount,
				label: 'Total assets value:'
			},
			{
				type: 'currency',
				value: monthlyPayment,
				label: 'Monthly payment:'
			},
			{
				type: 'currency',
				value: currentOutstandingBalance,
				label: 'Outstanding balance:'
			},
			{
				type: 'rate',
				value: currentInterestRate,
				label: 'Current interest rate:'
			}
		];

		const formattedCollection = formatCollection(valuesToFormat);
		const advisorNotes = `Advisor's name: ${advisorName}
				Advisor's email: ${advisorEmail}
				Advisor's company: ${advisorCompany}
				Credit Score: ${creditScoreValue}
				Client's name: ${clientFullName}
				Client's dob: ${dob}
				${formattedCollection}
				Employment Status: ${employmentStatus}
				Employer Name: ${employer}
				Employment Start Date: ${startDate}
				Highest degree obtained: ${educationLevel}
				School attended: ${school}
				Graduation year: ${graduationDate}
				Rent or own: ${rentOrOwn}
				Address: ${addressLine1} ${addressLine2} ${city}, ${state} ${zipCode}
				Current loan type: ${currentLoanType}

				------------------------------------------------------------------------------------------------
				Advisor's notes: ${additionalNotes}`;

		createDeal.mutate({
			advisorInfo: {
				advisorName,
				email: advisorEmail,
				companyName: advisorCompany,
				companyDomain: advisorWebsite
			},
			properties: {
				amount: '',
				dealname: `${advisorName} / ${clientFullName}`,
				dealtype: loanType,
				borrower: clientFullName,
				closedate: closeDate
			},
			noteInfo: {
				hs_note_body: advisorNotes
			}
		});
		// If we're at /loan-inquiry, perform the following
		if (!atLoanRequestDetailsUrl) {
			submitNewLoanInquiry();
			sendFormData();
		}
		// Else if we're at /loan-inquiry-details, perform the following
		else if (atLoanRequestDetailsUrl) {
			// Call new mutations here patching the new loan inquiry
			updateLoanRequest();
		}
	};

	// Handlers for mutation
	const { isError: sendFormDataError } = sendFormData;

	return (
		<>
			{!atLoanRequestDetailsUrl && (
				<Grid item xs={12}>
					<Typography variant="h2Gascogne">
						Student Loan Refinance
					</Typography>

					<Typography variant="body2">
						Fill in the information below to submit a loan request
						to Sora. You will be able to complete and review your
						loan request in the dashboard task bar.
					</Typography>
				</Grid>
			)}

			{isLoading || (savingLoanInquiry && <Loader />)}
			{!isLoading && !savingLoanInquiry && (
				<>
					{sendFormDataError && <Alert variant="error" />}

					{/* Modal shown on successful submission of  */}
					<LoanInquirySuccessModal isOpen={isSuccessModalOpen} />
					<Grid item xs={12} md={6}>
						{/* Date of Birth */}
						<StandardDatePicker
							id="dob"
							label="Date of birth"
							helperText="Enter your birth date"
							onChange={setDob}
							value={dob}
							error={dob === null || !isValidDate}
							errorText="Date of birth needs to be a valid date"
							inputProps={{
								'data-test': 'dateOfBirth'
							}}
						/>
					</Grid>

					<Grid item xs={12} md={6}>
						{/* Employment Status */}
						<Dropdown
							items={EMPLOYMENT_STATUS_OPTIONS}
							selected={employmentStatus}
							onChange={setEmploymentStatus}
							variant="outlined"
							label="Employment status"
						/>
					</Grid>

					<Grid item xs={12} md={6}>
						{/* Employer */}
						<Input
							label="Employer name"
							value={employer}
							onChange={setEmployer}
							type={TEXT}
							inputProps={{
								'data-test': 'employer'
							}}
						/>
					</Grid>

					<Grid item xs={12} md={6}>
						{/* Start date */}
						<StandardDatePicker
							id="startDate"
							label="Employment start date"
							onChange={setStartDate}
							value={startDate}
							error={startDate === null || !isValidDate}
							errorText="Start date needs to be a valid date"
							inputProps={{
								'data-test': 'startDate'
							}}
						/>
					</Grid>

					<Grid item xs={12} md={6}>
						{/* Education Level */}
						<Dropdown
							items={EDUCATION_LEVEL_OPTIONS}
							selected={educationLevel}
							onChange={setEducationLevel}
							variant="outlined"
							label="Highest degree obtained"
						/>
					</Grid>

					<Grid item xs={12} md={6}>
						{/* School Attended */}
						<Input
							inputProps={{
								'data-test': 'school'
							}}
							type={TEXT}
							label="School attended"
							value={school}
							onChange={setSchool}
							subLabel="Name of school"
						/>
					</Grid>

					<Grid item xs={12} md={6}>
						{/* Graduation Month and Year */}
						<YearMonthDatePicker
							helperText="Enter a year like 2022"
							inputFormat="yyyy"
							label="Graduation month and year"
							onChange={setGraduationDate}
							variant="outlined"
							value={graduationDate}
							views={GRADUATION_YEAR_ONLY_VIEW}
							inputProps={{
								'data-test': 'monthAndYearOfGraduation'
							}}
						/>
					</Grid>

					<Grid item xs={12} md={6}>
						{/* Rent or own */}
						<Dropdown
							items={['Rent', 'Own']}
							selected={rentOrOwn}
							onChange={setRentOrOwn}
							variant="outlined"
							label="Do you rent or own your primary residences?"
						/>
					</Grid>

					<Grid item xs={12} md={6}>
						{/* Monthly Payment Amount */}
						<CurrencyTextInput
							label="Monthly payment"
							value={monthlyPayment}
							onChange={setMonthlyPayment}
						/>
					</Grid>

					{/* Current Address */}
					<GetFullAddressPartialForm
						label="Current address"
						addressLine1={addressLine1}
						addressLine2={addressLine2}
						city={city}
						state={state}
						zipCode={zipCode}
						setAddressLine1={setAddressLine1}
						setAddressLine2={setAddressLine2}
						setCity={setCity}
						setState={setState}
						setZipCode={setZipCode}
					/>

					<Grid item xs={12}>
						<Typography variant="h3Gascogne">
							Current loan details
						</Typography>
					</Grid>

					<Grid item xs={12} md={6}>
						{/* Loan Type */}
						<Dropdown
							dataTestTag="loanType"
							items={['federal', 'private']}
							selected={currentLoanType}
							onChange={setCurrentLoanType}
							variant="outlined"
							label="Lender type"
						/>
					</Grid>

					<Grid item xs={12} md={6}>
						{/* Outstanding Balance */}
						<OutstandingBalanceInput
							label="Outstanding balance of current loan"
							outstandingBalance={loanAmount}
							setOutstandingBalance={setLoanAmount}
							inputProps={{
								'data-test': 'outstandingBalance'
							}}
						/>
					</Grid>
					<Grid item xs={12} md={6}>
						{/* Current Loan Interest Rate */}
						<RateInput
							rate={currentInterestRate}
							setRate={setCurrentInterestRate}
							label="Current interest rate *"
							inputProps={{
								'data-test': 'currentInterestRate'
							}}
						/>
					</Grid>
					<Grid item xs={12}>
						<SoraTextField
							label="Additional notes"
							value={additionalNotes}
							onChange={setAdditionalNotes}
						/>
					</Grid>
				</>
			)}

			<Grid item xs={12}>
				<PrimaryButton disabled={!isFormReady} onClick={submitForm}>
					Submit
				</PrimaryButton>
			</Grid>
		</>
	);
}

export default StudentLoanRefinanceForm;
